import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Container,
  IconButton,
  Link,
  Typography,
  useTheme,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import WorkIcon from "@mui/icons-material/Work";
import ArticleIcon from "@mui/icons-material/Article";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const CAROUSEL_FEED = {
  id: "indian",
  path: "/api/rss/indian",
  url: "https://www.moneycontrol.com/rss/MCtopnews.xml",
};

const FEEDS = [
  {
    id: "govtemployee",
    label: "Latest Updates for Government Employees",
    path: "/api/rss/staffnews",
    url: "https://www.staffnews.in/feed",
    icon: ArticleIcon,
    layout: "twoRows",
  },
  {
    id: "jobs",
    label: "Indian Central Government Jobs",
    paths: ["/api/rss/jobs1", "/api/rss/jobs2", "/api/rss/jobs3", "/api/rss/jobs4"],
    urls: [
      "https://www.govtjobsblog.in/feed",
      "https://indiajoblive.com/feed",
      "https://www.govtjobsdiary.com/feed",
      "http://results.amarujala.com/rss/results-alert.xml",
    ],
    icon: WorkIcon,
  },
];

// Use backend API first (no CORS). Only try proxies if API fails (e.g. dev server without backend).
const CORS_PROXIES = [
  (url) => `https://corsproxy.io/?${encodeURIComponent(url)}`,
  (url) => `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`,
];

// Placeholder when feed has no image (SVG data URI – always loads, no CORS)
const PLACEHOLDER_IMAGE =
  "data:image/svg+xml," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="600" height="400" viewBox="0 0 600 400"><defs><linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" style="stop-color:%23117aae"/><stop offset="100%" style="stop-color:%230f2854"/></linearGradient></defs><rect width="600" height="400" fill="url(#g)"/><text x="300" y="200" font-family="Arial,sans-serif" font-size="24" fill="rgba(255,255,255,0.9)" text-anchor="middle" dy=".3em">News</text></svg>'
  );

function getTextFromItem(item, tagName) {
  const el = item.querySelector(tagName);
  if (el) return el.textContent.trim();
  const byTag = item.getElementsByTagName(tagName);
  return byTag.length ? byTag[0].textContent.trim() : "";
}

function extractImageFromHtml(html, baseUrl) {
  if (!html || typeof html !== "string") return "";
  let src = "";
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const img = doc.querySelector("img");
    if (img) {
      src = img.getAttribute("src") || img.getAttribute("data-src") || "";
      src = src.trim();
    }
    if (!src) {
      const meta = doc.querySelector('meta[property="og:image"]');
      src = meta?.getAttribute("content")?.trim() || "";
    }
  } catch (_) {}
  if (!src) {
    const imgMatch = html.match(/<img[^>]+(?:src|data-src)=["']([^"']+)["']/i);
    if (imgMatch) src = imgMatch[1].trim();
  }
  if (!src) {
    const ogMatch = html.match(/<meta[^>]+property=["']og:image["'][^>]+content=["']([^"']+)["']/i) || html.match(/content=["']([^"']+)["'][^>]+property=["']og:image["']/i);
    if (ogMatch) src = ogMatch[1].trim();
  }
  if (!src) return "";
  if (src.startsWith("http://") || src.startsWith("https://")) return src;
  try {
    if (baseUrl) return new URL(src, baseUrl).href;
    return src;
  } catch {
    return src;
  }
}

function parseRssXml(xmlText) {
  if (!xmlText || typeof xmlText !== "string") return [];
  const trimmed = xmlText.trim();
  const looksLikeXml =
    trimmed.startsWith("<?xml") ||
    trimmed.startsWith("<rss") ||
    trimmed.startsWith("<feed") ||
    trimmed.includes("<item>") ||
    trimmed.includes("<entry>");
  if (!looksLikeXml) return [];

  const parser = new DOMParser();
  const doc = parser.parseFromString(xmlText, "text/xml");
  const channel = doc.querySelector("channel");
  let itemEls = channel ? channel.querySelectorAll("item") : doc.querySelectorAll("item");
  const isAtom = itemEls.length === 0;
  if (isAtom) itemEls = doc.querySelectorAll("entry");
  if (!itemEls || itemEls.length === 0) return [];

  const items = [];
  itemEls.forEach((item) => {
    const getText = (tag) => getTextFromItem(item, tag);
    const title = getText("title");
    if (!title) return;

    let link = getText("link");
    if (isAtom && !link) {
      const linkEl = item.querySelector("link");
      if (linkEl) link = linkEl.getAttribute("href") || "";
    }

    const enclosure = item.querySelector("enclosure");
    const MEDIA_NS = "http://search.yahoo.com/mrss/";
    const mediaContent =
      item.getElementsByTagNameNS?.(MEDIA_NS, "content")?.[0] ||
      item.querySelector("media\\:content");
    const mediaThumb =
      item.getElementsByTagNameNS?.(MEDIA_NS, "thumbnail")?.[0] ||
      item.querySelector("media\\:thumbnail") ||
      item.querySelector("thumbnail");
    let image = "";
    if (enclosure && enclosure.getAttribute("type")?.startsWith("image/")) {
      image = enclosure.getAttribute("url") || "";
    }
    if (!image && mediaContent) image = mediaContent.getAttribute("url") || "";
    if (!image && mediaThumb) image = mediaThumb.getAttribute("url") || "";

    const description = getText("description") || getText("summary");
    const contentEncoded =
      item.getElementsByTagNameNS?.("http://purl.org/rss/1.0/modules/content/", "encoded")?.[0]?.textContent?.trim() ||
      item.querySelector("content\\:encoded")?.textContent?.trim();
    const htmlContent = contentEncoded || description;
    if (!image && htmlContent) {
      image = extractImageFromHtml(htmlContent, link || undefined);
    }

    if (image && link && !image.startsWith("http")) {
      try {
        image = new URL(image, link).href;
      } catch (_) {}
    }

    items.push({
      title,
      link,
      description,
      pubDate: getText("pubDate") || getText("published") || getText("updated"),
      image,
    });
  });
  return items;
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = now - d;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffHours < 1) return "Just now";
    if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
    return d.toLocaleDateString(undefined, { dateStyle: "medium" });
  } catch {
    return dateStr;
  }
}

function isXmlResponse(text) {
  const t = text && text.trim();
  return t && (t.startsWith("<?xml") || t.startsWith("<rss") || t.startsWith("<feed") || t.includes("<item>") || t.includes("<entry>"));
}

async function fetchFeed(apiPath, directUrl) {
  try {
    const res = await fetch(apiPath);
    const text = await res.text();
    if (res.ok && isXmlResponse(text)) return text;
  } catch (_) {}

  for (const proxy of CORS_PROXIES) {
    try {
      const proxyUrl = proxy(directUrl);
      const res = await fetch(proxyUrl);
      let text = await res.text();
      if (typeof text === "string" && text.trim().startsWith("{")) {
        try {
          const j = JSON.parse(text);
          text = j.contents || j.content || text || "";
        } catch (_) {}
      }
      if (isXmlResponse(text)) return text;
    } catch (_) {}
  }
  throw new Error("Failed to load feed");
}

function CarouselSlideImage({ imageUrl, theme, primary }) {
  const [error, setError] = React.useState(false);
  const src = imageUrl || PLACEHOLDER_IMAGE;
  const showImg = !error;
  return showImg ? (
    <Box
      component="img"
      src={src}
      alt=""
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setError(true)}
      sx={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
      }}
    />
  ) : (
    <Box
      component="img"
      src={PLACEHOLDER_IMAGE}
      alt=""
      sx={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
      }}
    />
  );
}

function NewsSection({ label, Icon, items, loading, error, theme, layout }) {
  const cardSx = {
    mb: 3,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 2,
    overflow: "hidden",
  };

  const isTwoRows = layout === "twoRows";

  if (loading) {
    return (
      <Card elevation={0} sx={cardSx}>
        <Box sx={{ px: 2, pt: 2, pb: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <Icon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
          <Typography variant="h6" fontWeight={600} color="text.primary">
            {label}
          </Typography>
        </Box>
        <CardContent sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={32} />
        </CardContent>
      </Card>
    );
  }
  if (error) {
    return (
      <Card elevation={0} sx={cardSx}>
        <Box sx={{ px: 2, pt: 2, pb: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <Icon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
          <Typography variant="h6" fontWeight={600} color="text.primary">
            {label}
          </Typography>
        </Box>
        <CardContent>
          <Typography color="text.secondary">{error}</Typography>
        </CardContent>
      </Card>
    );
  }
  if (!items?.length) {
    return (
      <Card elevation={0} sx={cardSx}>
        <Box sx={{ px: 2, pt: 2, pb: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <Icon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
          <Typography variant="h6" fontWeight={600} color="text.primary">
            {label}
          </Typography>
        </Box>
        <CardContent>
          <Typography color="text.secondary">No articles in this section.</Typography>
        </CardContent>
      </Card>
    );
  }

  if (isTwoRows) {
    const displayItems = items.slice(0, 8);
    return (
      <Card elevation={0} sx={cardSx}>
        <Box sx={{ px: 2, pt: 2, pb: 1, display: "flex", alignItems: "center", gap: 1 }}>
          <Icon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
          <Typography variant="h6" fontWeight={600} color="text.primary">
            {label}
          </Typography>
        </Box>
        <Box
          component={CardContent}
          sx={{
            pt: 0,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
            gap: 2,
            px: 2,
            pb: 2,
          }}
        >
          {displayItems.map((item, idx) => (
            <Card
              key={item.link || idx}
              component={item.link ? "a" : "div"}
              href={item.link || undefined}
              target={item.link ? "_blank" : undefined}
              rel={item.link ? "noopener noreferrer" : undefined}
              elevation={0}
              sx={{
                textDecoration: "none",
                color: "inherit",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: "hidden",
                transition: "box-shadow 0.2s, border-color 0.2s",
                "&:hover": { boxShadow: 2, borderColor: theme.palette.primary.main },
              }}
            >
              <CardMedia
                component="img"
                height="120"
                image={item.image || PLACEHOLDER_IMAGE}
                alt=""
                referrerPolicy="no-referrer"
                sx={{ objectFit: "cover", bgcolor: theme.palette.action.hover }}
              />
              <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  {formatDate(item.pubDate)}
                </Typography>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {item.title}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Card>
    );
  }

  const columns = [];
  for (let i = 0; i < items.length; i += 2) {
    columns.push({ primary: items[i], secondary: items[i + 1] || null });
  }
  const displayColumns = columns.slice(0, 4);

  return (
    <Card elevation={0} sx={cardSx}>
      <Box sx={{ px: 2, pt: 2, pb: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <Icon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
        <Typography variant="h6" fontWeight={600} color="text.primary">
          {label}
        </Typography>
      </Box>
      <Box
        component={CardContent}
        sx={{
          pt: 0,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
          gap: 2,
          px: 2,
          pb: 2,
        }}
      >
        {displayColumns.map((col, idx) => (
          <Box key={idx} sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Card
              component={col.primary.link ? "a" : "div"}
              href={col.primary.link || undefined}
              target={col.primary.link ? "_blank" : undefined}
              rel={col.primary.link ? "noopener noreferrer" : undefined}
              elevation={0}
              sx={{
                textDecoration: "none",
                color: "inherit",
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: "hidden",
                transition: "box-shadow 0.2s, border-color 0.2s",
                "&:hover": { boxShadow: 2, borderColor: theme.palette.primary.main },
              }}
            >
              <CardMedia
                component="img"
                height="140"
                image={col.primary.image || PLACEHOLDER_IMAGE}
                alt=""
                referrerPolicy="no-referrer"
                sx={{ objectFit: "cover", bgcolor: theme.palette.action.hover }}
              />
              <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                  {formatDate(col.primary.pubDate)}
                </Typography>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {col.primary.title}
                </Typography>
              </CardContent>
            </Card>
            {col.secondary && (
              <Link
                href={col.secondary.link}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  textDecoration: "none",
                  color: "inherit",
                  display: "block",
                  "&:hover": { color: theme.palette.primary.main },
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.25 }}>
                  {formatDate(col.secondary.pubDate)}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {col.secondary.title}
                </Typography>
              </Link>
            )}
          </Box>
        ))}
      </Box>
    </Card>
  );
}

function RssFeedPage() {
  const theme = useTheme();
  const sliderRef = useRef(null);
  const [state, setState] = useState({
    indian: { loading: true, error: null, items: [] },
    govtemployee: { loading: true, error: null, items: [] },
    jobs: { loading: true, error: null, items: [] },
  });

  useEffect(() => {
    const feedIds = ["indian", "govtemployee", "jobs"];
    let cancelled = false;

    const getConfig = (id) => FEEDS.find((f) => f.id === id) || (id === "indian" ? CAROUSEL_FEED : null);

    feedIds.forEach((feedId) => {
      const config = getConfig(feedId);
      if (!config) return;

      (async () => {
        try {
          let items = [];

          if (config.paths && config.urls && Array.isArray(config.paths) && Array.isArray(config.urls)) {
            const results = await Promise.allSettled(
              config.paths.map((path, i) => fetchFeed(path, config.urls[i] || config.urls[0]))
            );
            if (cancelled) return;
            results.forEach((result) => {
              if (result.status === "fulfilled" && result.value) {
                items = items.concat(parseRssXml(result.value));
              }
            });
            items.sort((a, b) => {
              const da = a.pubDate ? new Date(a.pubDate).getTime() : 0;
              const db = b.pubDate ? new Date(b.pubDate).getTime() : 0;
              return db - da;
            });
          } else {
            const xml = await fetchFeed(config.path, config.url);
            if (cancelled) return;
            items = parseRssXml(xml);
          }

          if (cancelled) return;
          setState((prev) => ({ ...prev, [feedId]: { loading: false, error: null, items } }));
        } catch (e) {
          if (!cancelled) {
            setState((prev) => ({
              ...prev,
              [feedId]: { loading: false, error: e.message || "Failed to load", items: [] },
            }));
          }
        }
      })();
    });

    return () => { cancelled = true; };
  }, []);

  const primary = theme.palette.primary.main;
  const carouselItems = state.indian.items || [];
  const carouselLoading = state.indian.loading;
  const showCarousel = carouselItems.length > 0 || carouselLoading;

  const sliderSettings = {
    dots: false,
    infinite: carouselItems.length > 1,
    speed: 500,
    slidesToShow: Math.min(3, Math.max(1, carouselItems.length)),
    slidesToScroll: 1,
    centerMode: carouselItems.length >= 3,
    centerPadding: "8px",
    arrows: false,
    responsive: [
      { breakpoint: 960, settings: { slidesToShow: 2, centerMode: false } },
      { breakpoint: 600, settings: { slidesToShow: 1, centerMode: false } },
    ],
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: theme.palette.background.default,
        pt: { xs: 10, md: 12 },
        pb: 6,
      }}
    >
      {/* Header carousel - same width as sections below */}
      {showCarousel && (
        <Container maxWidth="lg" sx={{ mb: 3 }}>
          <Box sx={{ position: "relative", width: "100%", minHeight: 320 }}>
            {carouselLoading && carouselItems.length === 0 ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 320, bgcolor: "grey.100", borderRadius: 1 }}>
                <CircularProgress sx={{ color: primary }} />
              </Box>
            ) : carouselItems.length === 0 ? null : (
              <>
            <IconButton
              onClick={() => sliderRef.current?.slickPrev()}
              sx={{
                position: "absolute",
                left: { xs: 4, md: 8 },
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2,
                bgcolor: "white",
                color: primary,
                boxShadow: 2,
                "&:hover": { bgcolor: "grey.100", color: primary },
              }}
              size="large"
            >
              <ChevronLeftIcon fontSize="large" />
            </IconButton>
            <IconButton
              onClick={() => sliderRef.current?.slickNext()}
              sx={{
                position: "absolute",
                right: { xs: 4, md: 8 },
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 2,
                bgcolor: "white",
                color: primary,
                boxShadow: 2,
                "&:hover": { bgcolor: "grey.100", color: primary },
              }}
              size="large"
            >
              <ChevronRightIcon fontSize="large" />
            </IconButton>
            <Box sx={{ width: "100%", overflow: "hidden" }}>
            <Slider ref={sliderRef} {...sliderSettings}>
              {carouselItems.map((item, idx) => (
                <Box key={item.link || idx} sx={{ px: 0.5, outline: "none" }}>
                  <Box
                    component="a"
                    href={item.link || "#"}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: "block",
                      position: "relative",
                      height: 280,
                      borderRadius: 1,
                      overflow: "hidden",
                      textDecoration: "none",
                      color: "inherit",
                      borderRight: "1px solid rgba(255,255,255,0.4)",
                    }}
                  >
                    <CarouselSlideImage
                      imageUrl={item.image}
                      theme={theme}
                      primary={primary}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        background: "linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 50%)",
                      }}
                    />
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        p: 2,
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          color: "white",
                          fontWeight: 600,
                          fontSize: { xs: "0.9rem", md: "1rem" },
                          lineHeight: 1.3,
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          flex: 1,
                          textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                        }}
                      >
                        {item.title}
                      </Typography>
                      <PlayCircleFilledIcon
                        sx={{
                          color: "white",
                          fontSize: 36,
                          flexShrink: 0,
                          filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.5))",
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
            ))}
          </Slider>
            </Box>
              </>
            )}
          </Box>
          {carouselItems.length > 0 && (
            <Box
              sx={{
                width: "100%",
                py: 1.25,
                px: 2,
                backgroundColor: "grey.900",
                color: "white",
                borderRadius: "0 0 4px 4px",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Indian finance news
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  ArthGyan
                </Typography>
              </Box>
            </Box>
          )}
        </Container>
      )}

      {/* Three sections */}
      <Container maxWidth="lg">
        {FEEDS.map((config) => (
          <NewsSection
            key={config.id}
            label={config.label}
            Icon={config.icon}
            items={state[config.id].items}
            loading={state[config.id].loading}
            error={state[config.id].error}
            theme={theme}
            layout={config.layout}
          />
        ))}
      </Container>
    </Box>
  );
}

export default RssFeedPage;
